// Generated types for VersioningOverlay
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { VersioningBranchReadAPI, VersioningBranchWriteAPI } from './versioningbranch';
import { VersioningBranchReadAPISchemaObject, VersioningBranchWriteAPISchemaObject } from './versioningbranch';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface VersioningOverlayReadAPI extends VersioningBranchReadAPI {
  /** OVERLAY: the branch this overlay sits on top of (plan.md Phase F). An overlay exists per user per branch, from the first edit until Commit. */
  base_branch?: string | null;
  /** OVERLAY: the commit on `base_branch` this overlay's ops sit on — that branch's head when the overlay was created, moved by Baseline. Commit is allowed only while it still IS the head. Null when the base branch had no commit yet. */
  base_commit_id?: string | null;
  /** OVERLAY: the base branch's ops already rebased into this overlay that a later read from `base_cursor` can return again — those stamped after it, while a transaction was still open (the cursor is the watermark, so an op that lands late is never skipped). The next Baseline drops them instead of rebasing them twice, which would mark an acknowledged override again. Written with `base_cursor`. */
  base_seen_op_ids?: string[] | null;
  /** OVERLAY: whose working overlay this is (the user identifier), stamped by the engine at create (`cow.default_lane_row`). The attribute the ABAC owner constraint `own` keys on (scope_permissions.yaml): the template groups read an overlay only when it is theirs; a row without one is nobody's (fail closed). Null for branches and proposals. */
  owner?: string | null;
  /** OVERLAY: JSON list of MARKS — changes of mine that a baseline re-wrote to override a change made since (plan.md Phase F). An automatic baseline happens while I am not looking, so they are kept here until I acknowledge them; Commit refuses while any is unseen. */
  marks?: string | null;
  /** CHANGE PROPOSAL metadata (67 §5e): the branch this proposal targets. Non-null marks the row as a proposal. */
  proposal_target?: string | null;
  /** Change proposal: proposing user. */
  proposal_author?: string | null;
  /** Change proposal: one-line summary. */
  proposal_summary?: string | null;
  /** Change proposal lifecycle: open | accepted | rejected | withdrawn. */
  proposal_status?: string | null;
  /** Change proposal: who accepted, rejected or withdrew it (their user id). */
  decided_by?: string | null;
  /** Change proposal: when it was decided (Neo4j clock). */
  decided_at?: number | null;
  /** Change proposal: the reason the decider gave, if any. */
  decision_reason?: string | null;
  /** Change proposal: the commit it landed as on its target (accepted onto a branch). */
  landed_commit_id?: string | null;
  /** How many commits `base_branch` has gained since `base_commit_id` that this overlay has NOT been baselined onto (manual mode, or an open conflict). 0 = baselined on the head. */
  new_commits?: number;
  /** JSON list of CONFLICTS — changes of mine that can no longer be applied to the branch. While any is open nothing moves, and Commit is refused; settle them through Baseline. */
  conflicts?: string | null;
  /** JSON list of REVERT CONFLICTS (versioning_oplog.md §7) — changes I reverted into this overlay that no longer fit (a place held by another record, a link or field changed since). Commit is refused while any is open; settle each by keeping what is there or taking the reverted state. Written only by the revert and its settling — never by Baseline, which keeps following the branch. */
  revert_conflicts?: string | null;
  /** How many of this overlay's copies the LAST baseline refreshed from the new base (the records the branch's new commits touched, or whose changes the rebase rewrote) — why a copy changed. The rest were kept as they were (the targeted baseline). */
  refreshed?: number;
}

export interface VersioningOverlayWriteAPI extends VersioningBranchWriteAPI {
  /** OVERLAY: the commit on `base_branch` this overlay's ops sit on — that branch's head when the overlay was created, moved by Baseline. Commit is allowed only while it still IS the head. Null when the base branch had no commit yet. */
  base_commit_id?: string | null;
  /** OVERLAY: JSON list of MARKS — changes of mine that a baseline re-wrote to override a change made since (plan.md Phase F). An automatic baseline happens while I am not looking, so they are kept here until I acknowledge them; Commit refuses while any is unseen. */
  marks?: string | null;
  /** Change proposal: one-line summary. */
  proposal_summary?: string | null;
  /** Change proposal lifecycle: open | accepted | rejected | withdrawn. */
  proposal_status?: string | null;
  /** How many commits `base_branch` has gained since `base_commit_id` that this overlay has NOT been baselined onto (manual mode, or an open conflict). 0 = baselined on the head. */
  new_commits?: number;
  /** JSON list of CONFLICTS — changes of mine that can no longer be applied to the branch. While any is open nothing moves, and Commit is refused; settle them through Baseline. */
  conflicts?: string | null;
  /** JSON list of REVERT CONFLICTS (versioning_oplog.md §7) — changes I reverted into this overlay that no longer fit (a place held by another record, a link or field changed since). Commit is refused while any is open; settle each by keeping what is there or taking the reverted state. Written only by the revert and its settling — never by Baseline, which keeps following the branch. */
  revert_conflicts?: string | null;
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const VersioningOverlayReadAPISchema: z.ZodType<VersioningOverlayReadAPI> = VersioningBranchReadAPISchemaObject.extend({
  base_branch: z.string().nullable().optional(),
  base_commit_id: z.string().nullable().optional(),
  base_seen_op_ids: z.array(z.string()).nullable().optional(),
  owner: z.string().nullable().optional(),
  marks: z.string().nullable().optional(),
  proposal_target: z.string().nullable().optional(),
  proposal_author: z.string().nullable().optional(),
  proposal_summary: z.string().nullable().optional(),
  proposal_status: z.string().nullable().optional(),
  decided_by: z.string().nullable().optional(),
  decided_at: z.number().nullable().optional(),
  decision_reason: z.string().nullable().optional(),
  landed_commit_id: z.string().nullable().optional(),
  new_commits: z.number().optional(),
  conflicts: z.string().nullable().optional(),
  revert_conflicts: z.string().nullable().optional(),
  refreshed: z.number().optional(),
}).passthrough();

export const VersioningOverlayWriteAPISchema: z.ZodType<VersioningOverlayWriteAPI> = VersioningBranchWriteAPISchemaObject.extend({
  base_commit_id: z.string().nullable().optional(),
  marks: z.string().nullable().optional(),
  proposal_summary: z.string().nullable().optional(),
  proposal_status: z.string().nullable().optional(),
  new_commits: z.number().optional(),
  conflicts: z.string().nullable().optional(),
  revert_conflicts: z.string().nullable().optional(),
}).passthrough();
